import { ResetPasswordUser } from './../../shared/models/reset-password-user';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { IAuthCommon } from './../store/auth.interfaces';
import { LoginUser } from 'src/app/shared/models/login-user';
import { RequestAccess } from '../../shared/models/request-access';
import { StrataPlanRequest } from 'src/app/shared/models/action-request';
import { StrataResponse } from 'src/app/shared/models/strata-response';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(private http: HttpClient) { }

  /**
   * Login user to member site
   * @param loginUser LoginUser object
   */
  login(loginUser: LoginUser): Observable<IAuthCommon> {
    return this.http
      .post<any>(`${environment.api.root}/${environment.api.login}`, {
        data: { userName: loginUser.userName, password: loginUser.password },
      })
      .pipe(
        map((res) => {
          return res;
        }),
        catchError(this.handleError)
      );
  }

  public remoteLogin(encryptedUrl: string): Observable<any> {
    return this.http
      .post<any>(
        `${environment.api.root}/${environment.api.remote_login}`, { data: encryptedUrl }
      ).pipe(
        map((res) => {
          return res;
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Reset password
   * @param resetPasswordUser ResetPasswordUser
   */
  resetPassword(resetPasswordUser: ResetPasswordUser): Observable<string> {
    return this.http
      .post<any>(`${environment.api.root}/${environment.api.reset_password}`, {
        data: { currentPassword: resetPasswordUser.currentPassword, newPassword: resetPasswordUser.newPassword },
      })
      .pipe(
        map((res) => {
          return res; // this isn't going to be consumed
        }),
        catchError(this.handleError)
      );
  }

  forgotPassword(username: string): Observable<string> {
    return this.http
      .post<any>(`${environment.api.root}/${environment.api.forgot_password}`, {
        data: { username },
      })
      .pipe(
        map((res) => {
          return res; // this isn't going to be consumed
        }),
        catchError(this.handleError)
      );
  }


  fetchWebAddress(planNumber: string): Observable<any> {
    return this.http
      .post<any>(`${environment.api.root}/${environment.api.web_address}`, {
        strataPlanNumber: planNumber,
      })
      .pipe(
        map((res) => {
          return res.data;
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Request access to member site
   * @param requestAccess RequestAccess object
   */
  signUp(requestAccess: RequestAccess): Observable<string> {
    const formData = new FormData();
    formData.append('displayName', requestAccess.displayName);
    formData.append('email', requestAccess.email);
    formData.append('firstName', requestAccess.firstName);
    formData.append('lastName', requestAccess.lastName);
    formData.append('password', requestAccess.password);
    formData.append('roleId', requestAccess.roleId.toString());
    formData.append('unitId', requestAccess.unitId.toString());
    formData.append('username', requestAccess.username);
    formData.append('document', requestAccess.document);
    return this.http
      .post<any>(
        `${environment.api.root}/${environment.api.resident_profile}`,
        formData)
      .pipe(
        map((res) => res),
        catchError(this.handleError)
      );
  }

  refreshToken(rt: string) {
    return this.http
      .post<any>(`${environment.api.root}/${environment.api.refresh_token}`, {
        data: { refreshToken: rt },
      })
      .pipe(
        map((res) => {
          return res;
        }),
        catchError(this.handleError)
      );
  }


  public getActionStatus(unitId: number): Observable<any> {
    return this.http
      .get<any>(
        `${environment.api.root}/${environment.api.action_request}/${environment.api.request_status}/${unitId}`
      ).pipe(
        map((res) => {
          return res.data;
        })
      );
  }

  private handleError(err) {
    // in a real world app, we may send the server to some remote logging infrastructure
    // instead of just logging it to the console
    let errorMessage: string;
    if (err && err.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      errorMessage = `An error occurred: ${err.error.message}`;
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      errorMessage = `Backend returned code ${err.status}: ${err.code}`;
      if (err.error) {
        errorMessage = err.error;
      }
    }
    return throwError(errorMessage);
  }
}
